/**
 * Asking the image server for a picture at the size an element actually shows it at.
 *
 * Every thumbnail URL this product hands a client is `/files/{uid}` — the byte route, which serves
 * the still whole, at whatever the pipeline stored (a video's is 1280 on its long side, a display
 * rendition 3840). A 48-pixel tile downloading that is the cost this module removes:
 * `/images/files/{uid}/{fit}-{W}x{H}.{ext}` answers the same picture resized, on the **same
 * signature** — the `sig` is carried across from the file URL, because both doors redeem one
 * capability over one file uid.
 *
 * # The box is the caller's and the rungs are the server's
 *
 * Sizes come from a fixed ladder (`backend/crates/bin/images/src/variant.rs`), so one picture has a
 * bounded set of addresses and a CDN can hold them. A request off the ladder is answered with a
 * `308` to the rung that covers it — correct, but a round trip per tile — so {@link rungCovering}
 * snaps here and every address this module builds is the canonical one.
 *
 * **A 16:9 box wants a 16:9 pair of rungs.** Each side is snapped independently, so asking for
 * 480×270 gets 480×320 — a box that is no longer 16:9, which under `cover` crops the sides of a
 * still that did not need cropping. The eight standard frames are on the ladder for this reason
 * (160×90, 320×180, 640×360, 960×540, 1280×720 …) and a caller showing a 16:9 tile should name one
 * of them.
 *
 * **What each caller passes is its element's box at about twice the pixel density**, so a tile is
 * not soft on a dense screen, and the fit its CSS means.
 */

/** The sizes a variant's sides are drawn from — `videas_image_server::variant::LADDER`, ascending. */
const LADDER = [90, 160, 180, 240, 320, 360, 480, 540, 640, 720, 960, 1080, 1280, 1440, 1920, 2160, 2560, 3840]

/**
 * How a picture is fitted to the box: `cover` fills it and crops the middle out, `contain` puts the
 * whole picture inside it. Neither ever enlarges.
 */
export type ThumbnailFit = 'cover' | 'contain'

/**
 * `[origin]/files/{uid}[?query]`, the one address a thumbnail arrives as — signed by the server
 * (`?sig=…`) or bare for a file that needs none.
 */
const FILE_URL = /^((?:[a-z][a-z0-9+.-]*:)?\/\/[^/]+)?\/files\/([^/?#]+)(\?[^#]*)?$/i

/** The smallest rung that covers `side`, and the largest for anything past the ladder. */
function rungCovering(side: number): number {
  return LADDER.find((rung) => rung >= side) ?? LADDER[LADDER.length - 1]!
}

/**
 * Whether this browser takes WebP — smaller than JPEG at the same quality, and the only one of the
 * three the server writes that keeps an alpha channel, so a still cut from a picture with
 * transparency is not flattened onto white.
 *
 * The canvas encoder is what is asked, because it answers synchronously and a URL is built in a
 * template. It is the stricter question — a browser can decode WebP without encoding it, which is
 * every Safari between 14 and 16 — so the answer errs towards JPEG: a few more bytes, never a
 * picture that does not load. Asked once and kept.
 */
let acceptsWebP: boolean | undefined

function webPAccepted(): boolean {
  if (acceptsWebP === undefined) {
    try {
      acceptsWebP = document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp')
    } catch {
      acceptsWebP = false
    }
  }
  return acceptsWebP
}

/**
 * The same picture at the size this element shows it, or the value unchanged when it is not a
 * `/files/{uid}` address — a provider's own poster, an absolute URL on somebody else's host — and
 * `undefined` when there is no picture at all.
 *
 * `width` and `height` are the box wanted in device pixels; each is snapped up to the ladder.
 */
export function withThumbnailSize(
  url: string | null | undefined,
  width: number,
  height: number,
  fit: ThumbnailFit = 'cover',
): string | undefined {
  if (!url) return undefined
  const match = FILE_URL.exec(url)
  if (!match) return url
  const [, origin = '', uid, query = ''] = match
  const box = `${rungCovering(width)}x${rungCovering(height)}`
  const extension = webPAccepted() ? 'webp' : 'jpg'
  // The query is carried verbatim: the `sig` in it is the file-access token over this same uid,
  // which the image server checks exactly as the byte route does.
  return `${origin}/images/files/${uid}/${fit}-${box}.${extension}${query}`
}
